import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {getRootStore} from '../../app.bootstrap';

import './styles.scss';

export interface TsShareMultipleResourcesProps {
    resourceType: 'images' | 'history' | string;
    images: any[];
    history: any[];

    // Dashboard-related methods passed as props
    areAllResourcesSelected: boolean;
    selectUnselectAllResources: (resourceType: string, data: {history: any[]; images: any[]}, select?: boolean) => void;
    enableLocalImageSaving: boolean;
    downloadResources: (resourceType: string, data: {history: any[]; images: any[]}) => void;
    resourcesReadyToBeShared: boolean;
    enableCopyToClipboard: boolean;
    copyResourcesToClipboard: (resourceType: string) => void;
    sendResourcesToClient: () => void;
    downloadAndEmailSharing: boolean;
    enableEmailImageSharing: boolean;
    sendResourcesByEmail: (resourceType: string) => void;
    downloadResourcesAndOpenEmail: (resourceType: string, data: {history: any[]; images: any[]}) => void;
}

@observer
export class TsShareMultipleResources extends TechseeBaseComponent<TsShareMultipleResourcesProps> {
    private translate = getRootStore().localizationService.translate;

    private getDataObject() {
        const {images, history} = this.props;
        return {history: history || [], images: images || []};
    }

    private safeClick(handler: (e?: any) => void, disabled?: boolean) {
        return async (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) {
                return;
            }
            await handler(e);
        };
    }

    renderInternal(): JSX.Element {
        const {
            resourceType,
            selectUnselectAllResources,
            enableLocalImageSaving,
            downloadResources,
            enableCopyToClipboard,
            copyResourcesToClipboard,
            sendResourcesToClient,
            downloadAndEmailSharing,
            enableEmailImageSharing,
            sendResourcesByEmail,
            downloadResourcesAndOpenEmail,
            resourcesReadyToBeShared,
            areAllResourcesSelected
        } = this.props;

        const allSelected = areAllResourcesSelected;
        const dataObj = this.getDataObject();
        const localSaveEnabled = enableLocalImageSaving;
        const copyEnabled = enableCopyToClipboard;
        const emailEnabled = enableEmailImageSharing;
        const readyToShare = resourcesReadyToBeShared;

        return (
            <div className='ts-share-multiple-resources'>
                <div className='sharing-images-container'>
                    <a
                        className={`btn btn-light ${allSelected ? 'tb tb-check_all' : 'tb tb-check'}`}
                        onClick={this.safeClick(() => {
                            selectUnselectAllResources(resourceType, dataObj, false);
                        })}
                        title={this.translate('DASHBOARD.VIEW.SELECT_ALL')}
                    />

                    {localSaveEnabled && (
                        <a
                            className={`btn btn-light tb tb-download ${readyToShare ? '' : 'disabled'}`}
                            title={this.translate('DASHBOARD.VIEW.DOWNLOAD_ALL')}
                            onClick={this.safeClick(() => downloadResources(resourceType, dataObj), !readyToShare)}
                        />
                    )}

                    {copyEnabled && (
                        <a
                            className={`btn btn-light tb tb-copy ${readyToShare ? '' : 'disabled'}`}
                            title={this.translate('DASHBOARD.VIEW.COPY_TO_CLIPBOARD_ALL')}
                            onClick={this.safeClick(() => copyResourcesToClipboard(resourceType), !readyToShare)}
                        />
                    )}

                    <a
                        className={`btn btn-light tb tb-send ${readyToShare ? '' : 'disabled'}`}
                        title={this.translate('DASHBOARD.VIEW.SEND_ALL')}
                        onClick={this.safeClick(() => sendResourcesToClient(), !readyToShare)}
                    />

                    {!downloadAndEmailSharing && emailEnabled && (
                        <a
                            className={`btn btn-light tb tb-email ${readyToShare ? '' : 'disabled'}`}
                            title={this.translate('DASHBOARD.VIEW.SEND_ALL_BY_EMAIL')}
                            onClick={this.safeClick(() => sendResourcesByEmail(resourceType), !readyToShare)}
                        />
                    )}

                    {downloadAndEmailSharing && emailEnabled && (
                        <a
                            className={`btn btn-light tb tb-downloadAndEmail ${readyToShare ? '' : 'disabled'}`}
                            title={this.translate('DASHBOARD.VIEW.SEND_ALL_BY_EMAIL')}
                            onClick={this.safeClick(
                                () => downloadResourcesAndOpenEmail(resourceType, dataObj),
                                !readyToShare
                            )}
                        />
                    )}
                </div>
                <hr className='row-header' />
            </div>
        );
    }
}
